import { useState } from "react";
import { X, Spinner, CheckCircle } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = [
  { value: "", label: "Select a category…", disabled: true },
  // Patient + doctor categories
  { value: "booking_issue", label: "Booking issue" },
  { value: "cancellation_reschedule_issue", label: "Cancellation / reschedule issue" },
  { value: "doctor_clinic_info_issue", label: "Doctor or clinic information issue" },
  { value: "medical_record_upload_issue", label: "Medical record upload issue" },
  { value: "procedure_request_issue", label: "Procedure request issue" },
  { value: "emergency_request_issue", label: "Emergency request issue" },
  { value: "queue_issue", label: "Queue issue" },
  { value: "profile_issue", label: "Profile issue" },
  { value: "subscription_trial_issue", label: "Subscription / trial issue" },
  { value: "patient_record_access_issue", label: "Patient record access issue" },
  { value: "login_account_issue", label: "Login or account issue" },
  { value: "app_bug", label: "App bug" },
  { value: "other", label: "Other" },
];

const PRIORITIES = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

export default function CreateTicketModal({ onClose, defaultCategory }) {
  const { user } = useAuth();
  const [category, setCategory] = useState(defaultCategory || "");
  const [priority, setPriority] = useState("medium");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!category || !subject.trim() || !description.trim()) {
      setError("Category, subject, and description are required");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/support/tickets", {
        category,
        priority,
        subject: subject.trim(),
        description: description.trim(),
        source: "web",
      });
      setSuccess(data.ticket);
    } catch (err) {
      setError(formatApiErrorDetail(err) || "Failed to create ticket");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={onClose}>
        <div className="w-full max-w-md rounded-2xl bg-card border border-border p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
          <div className="text-center py-4">
            <CheckCircle size={48} className="mx-auto text-green-500 mb-3" weight="fill" />
            <h3 className="text-lg font-display font-bold text-foreground">Ticket Created!</h3>
            <p className="text-sm text-muted-foreground mt-1">{success.ticket_number}</p>
            <p className="text-xs text-muted-foreground mt-2">Our support team will review it and get back to you.</p>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={onClose} className="flex-1 rounded-full border border-border py-2.5 text-sm font-semibold text-foreground hover:bg-muted transition-colors">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-card border border-border shadow-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h3 className="font-display font-bold text-lg text-foreground">Create Support Ticket</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Tell us what happened. Our support team will review it.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-muted text-muted-foreground transition-colors">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {error && (
            <div className="rounded-xl bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">Category *</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)}
              className="w-full h-10 rounded-xl border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value} disabled={c.disabled}>{c.label}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">Priority</label>
            <select value={priority} onChange={(e) => setPriority(e.target.value)}
              className="w-full h-10 rounded-xl border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
              {PRIORITIES.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">Subject *</label>
            <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
              placeholder="Brief summary of the issue" maxLength={200}
              className="w-full h-10 rounded-xl border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">Description *</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what happened in detail…" rows={5} maxLength={2000}
              className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none" />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 rounded-full border border-border py-2.5 text-sm font-semibold text-foreground hover:bg-muted transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className="flex-1 rounded-full bg-primary text-primary-foreground py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {submitting ? <><Spinner size={16} className="animate-spin" /> Submitting…</> : "Submit Ticket"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
